import { createSelector } from '@reduxjs/toolkit';
import assign from 'lodash/assign';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { newPermissions, newRolePermissions } from '@shared/newPermissions';
import { enforceRosterAccessExclusivity } from '@shared/utils/rosterPermissionExclusivity';

import { payloadSel } from '../../../location/duck/selectors';
import {
  Program,
  ProgramId,
  advancementModuleName,
  advancementReportModuleName,
  contextModuleName,
  coreModuleName,
  councilUnitsModuleName,
  dictModuleName,
  dictionaryIds,
  emptyArr,
  emptyObj,
  lgWidth,
  mdWidth,
  moduleName,
  offlineModuleName,
  packRosterModuleName,
  parentGuardian,
  programForUnitType,
  screenLg,
  screenMd,
  screenModuleName,
  screenXl,
  unitTypeIdForUnitType,
  userModuleName,
} from '../../constants';
import extractOrgGuidAndUserId from '../../utils/extractOrgGuidAndUserId';

const moduleSel = state => state[moduleName];
const coreModuleSel = state => state[coreModuleName];
const userModuleSel = state => state[userModuleName];

export const tablePageSizeSel = state => moduleSel(state).tablePageSize;
export const userPreferencesSel = (state, featureName) =>
  moduleSel(state).userPreferences[featureName];

// Core
export const appBootstrappedSel = state => coreModuleSel(state).appBootstrapped;
export const headerVisibleSel = state => coreModuleSel(state).headerVisible;
export const sidebarOpenSel = state => coreModuleSel(state).sidebarOpen;
export const screenModuleSel = state => state[screenModuleName];
export const minModeWidthSel = state => screenModuleSel(state).min;
export const screenModeSel = state => screenModuleSel(state).mode;
export const isScreenXlSel = state => screenModeSel(state) === screenXl;
export const isScreenAtLeastXlSel = state =>
  isScreenXlSel(state) || minModeWidthSel(state) > lgWidth;
export const sidebarExpandedSel = state =>
  isScreenAtLeastXlSel(state) && coreModuleSel(state).sidebarExpandedPreferred;
export const modalOpenSel = state => coreModuleSel(state).modalOpenCount !== 0;

// Dict
const dicModuleSel = state => state[dictModuleName];
const dataSel = state => dicModuleSel(state).data;
export const dictionarySel = (state, id) => dataSel(state)[id];
export const adventuresSel = state =>
  dictionarySel(state, dictionaryIds.ADVENTURES) || emptyArr;
export const adventuresByIdSel = createSelector(adventuresSel, adventures =>
  keyBy(adventures, 'id'),
);
export const allAwardsSel = state =>
  dictionarySel(state, dictionaryIds.AWARDS) || emptyArr;
//displays only active awards (not expired) and not adult awards
export const awardsSel = createSelector(allAwardsSel, awards =>
  awards.filter(
    ({ expiredDate, adultAward }) => !expiredDate && adultAward !== 'True',
  ),
);

export const awardsByIdSel = createSelector(awardsSel, awards =>
  keyBy(awards, 'id'),
);

export const ranksSel = state =>
  dictionarySel(state, dictionaryIds.RANKS) || emptyArr;

export const ranksByIdSel = createSelector(ranksSel, ranks =>
  keyBy(ranks, 'id'),
);

export const allSSElectivesSel = state =>
  dictionarySel(state, dictionaryIds.SSELECTIVES) || emptyArr;

export const ssElectivesSel = createSelector(allSSElectivesSel, ssElectives =>
  ssElectives.map(ssElective => ({
    ...ssElective,
    id: ssElective.ssElectiveId,
    name: ssElective.ssName,
  })),
);

export const cubRanksSel = createSelector(ranksSel, ranks =>
  ranks.filter(rank => +rank.programId === ProgramId.CUB_SCOUT),
);
export const meritBadgesSel = state =>
  dictionarySel(state, dictionaryIds.MERIT_BADGES) || emptyArr;
export const meritBadgesByIdSel = createSelector(meritBadgesSel, mbs =>
  keyBy(mbs, 'id'),
);

// User
export const loginDataSel = state => userModuleSel(state).loginData;
export const isLoggedInSel = createSelector(
  loginDataSel,
  loginData => !!(loginData && Object.keys(loginData).length > 0),
);
export const safeLoginDataSel = state => loginDataSel(state) || emptyObj;
/** @returns {string | null} */
export const personGuidSel = state => safeLoginDataSel(state).personGuid;
export const userIdSel = state => safeLoginDataSel(state).account?.userId;
export const userDataSel = state => userModuleSel(state).userData;
export const safeUserDataSel = state => userDataSel(state) || emptyObj;
export const masqueradeLoadingSel = state =>
  userModuleSel(state).masqueradeLoading;
export const masqueradingSel = state => userModuleSel(state).masquerading;
export const editWhileMasqueradingSel = state =>
  userModuleSel(state).editWhileMasquerading;
export const showDeleteEmailModalSel = state =>
  moduleSel(state).showDeleteEmailModal;
export const showDeleteAddressModalSel = state =>
  moduleSel(state).showDeleteAddressModal;
export const showDeletePhoneModalSel = state =>
  moduleSel(state).showDeletePhoneModal;
export const isLoadingDeleteEmailModalSel = state =>
  moduleSel(state).isLoadingDeleteEmailModal;
export const triggerAllRostersRequestSel = state =>
  moduleSel(state).triggerAllRostersRequest;

// Pack Roster
export const advancementModuleSel = state => state[advancementModuleName];
export const packRosterModuleSel = state =>
  advancementModuleSel(state)?.[packRosterModuleName];
export const packRosterLoadingSel = state => packRosterModuleSel(state).loading;
export const packRosterItemsSel = state => {
  /** @type {import('@modules/advancement/duck/types').RosterScout[]} */
  const result = packRosterModuleSel(state).items;
  return result;
};
export const packRosterItemsCountSel = createSelector(
  packRosterItemsSel,
  rosterItems => rosterItems?.length || 0,
);
export const rosterYouthSelectedSel = createSelector(
  packRosterModuleSel,
  rosterModule => rosterModule.selectedKeys || [],
);
export const packRosterAllItemsByMIDSel = createSelector(
  packRosterItemsSel,
  roster => {
    const result = keyBy(roster, item => {
      if (!item) return null;
      return +item.memberId;
    });
    return result;
  },
);

// advancementReport
export const advancementReportModuleSel = state =>
  state[advancementReportModuleName];
export const allReportSel = state =>
  advancementReportModuleSel(state).allReport;
export const reportInfoSel = state =>
  advancementReportModuleSel(state).reportInfo;
export const selectedUserIdsSel = state =>
  advancementReportModuleSel(state).userIds || [];
/** @returns Set<number> */
export const selectedUserIdsSetSel = state =>
  new Set((selectedUserIdsSel(state) ?? []).map(id => +id));
export const usersReportSel = createSelector(
  allReportSel,
  selectedUserIdsSetSel,
  (allReport, userIdsSet) => {
    if (userIdsSet.size === 0) {
      return allReport;
    }
    return allReport.filter(({ youth: { userId } }) => userIdsSet.has(+userId));
  },
);
export const notAwardedAdvancementsCountSel = state =>
  usersReportSel(state).length;
export const allNotAwardedAdvancementsCountSel = state =>
  allReportSel(state).length;

// screen
export const maxModeWidthSel = state => screenModuleSel(state).max;
export const isScreenMdSel = state => screenModeSel(state) === screenMd;
// alias for convenience
export const isMobileSel = state =>
  isScreenMdSel(state) || maxModeWidthSel(state) <= mdWidth;
export const isScreenLgSel = state => screenModeSel(state) === screenLg;
export const isScreenAtLeastLgSel = state =>
  isScreenLgSel(state) || minModeWidthSel(state) > mdWidth;

// Offline
const offlineModuleSel = state => state[offlineModuleName];
export const manualOfflineSel = state => offlineModuleSel(state).manualOffline;
export const offlineSel = state =>
  offlineModuleSel(state).offline || manualOfflineSel(state);

// councilUnits
const councilUnitsModuleSel = state => state[councilUnitsModuleName];
export const recentUnitsSel = state => councilUnitsModuleSel(state).recentUnits;

// Context
export const contextModuleSel = state => state[contextModuleName];
export const organizationPositionsSel = state => {
  const orgs = (userDataSel(state) || {}).organizations;
  return orgs || [];
};

const organizationsGuidsSel = createSelector(
  organizationPositionsSel,
  organizationPositions =>
    organizationPositions &&
    organizationPositions.map(({ organizationGuid }) => organizationGuid),
);
export const defaultOrganizationGuidSel = state =>
  contextModuleSel(state).defaultOrganizationGuid;

const safeDefaultOrganizationGuidSel = createSelector(
  organizationsGuidsSel,
  defaultOrganizationGuidSel,
  (organizationsGuids, defaultOrganizationGuid) => {
    if (!organizationsGuids || !organizationsGuids.length) {
      return null;
    }
    if (
      !defaultOrganizationGuid ||
      !organizationsGuids.includes(defaultOrganizationGuid)
    ) {
      return organizationsGuids[0];
    }
    return defaultOrganizationGuid;
  },
);

/**
 * GUID for the current or default organization i.e. the one selected on the dropdown.
 * If you are on the Parent/Guardian context then probably you want to use
 * `parentOrgGuidSel` instead.
 */
export const organizationGuidSel = state => {
  let organizationGuid =
    contextModuleSel(state).organizationGuid ||
    safeDefaultOrganizationGuidSel(state);
  const { orgGuid } = extractOrgGuidAndUserId(organizationGuid);
  return orgGuid;
};

export const childUserIdSel = state => {
  let organizationGuid =
    contextModuleSel(state).organizationGuid ||
    safeDefaultOrganizationGuidSel(state);
  const { userId } = extractOrgGuidAndUserId(organizationGuid);
  return userId;
};

/**
 * Returns the current org guid for the parent/guardian role selected.
 * Since the org GUID is <orgGuid>*<userId> you can control using the
 * `extractOrgGuid` to either just return `orgGuid` or both.
 *
 * @param {boolean} [extractOrgGuid] If `true`, returns only the org guid without the `userId`
 **/
export const parentOrgGuidSel = (state, extractOrgGuid = false) => {
  const organizationGuid = contextModuleSel(state).parentOrgGuid;
  const { orgGuid } = extractOrgGuidAndUserId(organizationGuid);
  return extractOrgGuid ? orgGuid : organizationGuid;
};

/**
 * Returns the `UserOrganizationInfo` for the currently-selected organization
 * (the one indicated by `parentOrgGuidSel`).
 *
 * Reads: `organizationPositionsSel` (all of the user's org memberships) and
 * `parentOrgGuidSel` (the selected org guid, including parent/guardian context).
 *
 * Returns the matching org entry, or an empty object `{}` when no org matches
 * BEWARE to treat the empty case as "no selected org".
 *
 * @returns {import('@modules/user/duck/types').UserOrganizationInfo}
 */
export const organizationPositionSel = createSelector(
  organizationPositionsSel,
  parentOrgGuidSel,
  (organizationPositions, selectedOrganizationGuid) => {
    /** @type {import('@modules/user/duck/types').UserOrganizationInfo} */
    const orgInfo =
      (organizationPositions &&
        organizationPositions.find(
          ({ organizationGuid }) =>
            organizationGuid === selectedOrganizationGuid,
        )) ||
      {};
    return orgInfo;
  },
);

export const isCouncilOrgActiveSel = createSelector(
  organizationPositionSel,
  org => org && org.isCouncil,
);

export const isCouncilOrgActiveOrgGuidSel = createSelector(
  organizationPositionSel,
  org => (org?.isCouncil ? org.organizationGuid : null),
);

export const childOrganizationSel = state =>
  contextModuleSel(state).childOrganization || emptyObj;

/**
 * Returns the `UserOrganizationInfo` for the currently-selected organization,
 * resolving the council/child-org case.
 *
 * BEWARE: don't use this selector to read a raw `organizationGuid` for API
 * calls. In Parent/Guardian contexts the guid is a composite that embeds the
 * youth MID, e.g. `"<orgGuid>*<mid>"`. Passing that to ESB endpoints returns a 403.
 *
 * Use `organizationGuidSel` instead.
 */
export const selectedOrganizationSel = createSelector(
  organizationPositionSel,
  childOrganizationSel,
  (
    organizationPosition,
    /** @type {import('@modules/user/duck/types').UserOrganizationInfo} */
    childOrganization,
  ) => {
    if (organizationPosition && organizationPosition.isCouncil) {
      return childOrganization;
    }
    return organizationPosition;
  },
);

export const isSelectedOrganizationValidSel = createSelector(
  selectedOrganizationSel,
  selectedOrganization =>
    !!selectedOrganization && !isEmpty(selectedOrganization),
);

/** @returns {number} Unit Type ID */
export const unitTypeIdSel = state =>
  (selectedOrganizationSel(state) || {}).unitTypeId;

export const organizationTypeIdSel = state =>
  (selectedOrganizationSel(state) || {}).organizationTypeId || 6; // default to 6 (Unit)

export const userRoleSel = state => selectedOrganizationSel(state).role || '';

export const useDenDetailsArrSel = state =>
  selectedOrganizationSel(state).denDetailsArr || [];

export const hasCouncilPositionSel = createSelector(
  userDataSel,
  user => user.isCouncil,
);

export const hasCouncilEditRightsActiveOrgSel = createSelector(
  organizationPositionSel,
  org => org.hasCouncilEditRights,
);

export const hasCouncilEditRightsSel = createSelector(
  userDataSel,
  user => user.hasCouncilEditRights,
);

export const isMeritBadgeCounselorSel = createSelector(
  organizationPositionSel,
  org => org.isMeritBadgeCounselor,
);

export const isMBCAndLeaderSel = createSelector(organizationPositionSel, org =>
  Boolean(org.isMBCAndLeader),
);

export const hasMeritBadgeCounselorPosSel = createSelector(
  organizationPositionsSel,
  orgs => orgs && orgs.some(org => org.isMeritBadgeCounselor),
);

export const isYouthMemberSel = createSelector(organizationPositionSel, org =>
  Boolean(org.isYouthMember),
);

export const isParentGuardianSel = createSelector(
  organizationPositionSel,
  org => Boolean(org.isParentGuardian),
);

export const isScoutbookRoleSel = createSelector(organizationPositionSel, org =>
  Boolean(org.isScoutbookRole),
);

export const isKey3Sel = createSelector(organizationPositionSel, org =>
  Boolean(org.isKey3),
);

export const isCouncilAdminSel = createSelector(organizationPositionSel, org =>
  Boolean(org.isCouncilAdmin),
);

export const isParentOrYouthMemberSel = createSelector(
  isYouthMemberSel,
  isParentGuardianSel,
  (isYouthMember, isParentGuardian) => isYouthMember || isParentGuardian,
);

export const youthMemberPersonGuidSel = createSelector(
  organizationPositionSel,
  isYouthMemberSel,
  isParentGuardianSel,
  (org, isYouthMember, isParentGuardian) =>
    (isParentGuardian || isYouthMember) && org.personGuid,
);

/**
 * @returns Permission object based on current organization/unit selected
 */
export const newPermissionsSel = createSelector(
  userDataSel,
  organizationPositionSel,
  (userData, orgInfo) => {
    // Use this for any permission that doesn't depend on roles or positions
    const initialPermissions = {};

    if (userData?.canMasquerade) {
      initialPermissions[newPermissions.MASQUERADE] = true;
    }

    // TODO: Deal with any other account without organizations or valid positions
    const userOrganization = userData?.organizations?.find(
      org => org.organizationGuid === orgInfo.organizationGuid,
    );

    const isParentGuardian = !!(
      userOrganization?.connection && userOrganization?.isParentGuardian
    );

    const allRoles = userOrganization?.allRoles;

    if (allRoles?.length) {
      let sanitizedAllRoles = [...allRoles];
      //We need to filter out Den chief if unit is a troop
      if (
        userOrganization.program === programForUnitType.Troop &&
        allRoles.includes('Den Chief')
      ) {
        sanitizedAllRoles = allRoles.filter(role => role !== 'Den Chief');
      }

      //We need to filter out Parent/Guardian when the current selected unit is for Leader purpuses
      if (!isParentGuardian && allRoles.includes(parentGuardian)) {
        sanitizedAllRoles = allRoles.filter(role => role !== parentGuardian);
      }

      const permsByPosition = sanitizedAllRoles.map(
        role => newRolePermissions[role] || {},
      );
      const mergedPerms = assign({ ...initialPermissions }, ...permsByPosition);
      // A user aggregating both a lite and a full-roster position in the same
      // org keeps the full roster; lite must yield to VIEW_ROSTER_PAGE.
      return enforceRosterAccessExclusivity(mergedPerms);
    }

    // For parents
    if (isParentGuardian) {
      return newRolePermissions[parentGuardian];
    }

    return { ...initialPermissions };
  },
);

/**
 * @returns {boolean} if it has some of the permissions passed
 */
export const hasNewPermissionSel = (state, ...permissions) => {
  const permissionsObject = newPermissionsSel(state);
  const isAllowed = permissions.some(
    permission => !!permissionsObject[permission],
  );
  return isAllowed;
};

/**
 * @returns Permission object keyed by organization GUID
 */
export const newOrgPermissionsSel = createSelector(userDataSel, userData =>
  userData?.organizations?.reduce(
    (
      acc,
      { organizationGuid, allRoles, program, connection, isParentGuardian },
    ) => {
      const isOrgParentGuardian = !!(connection && isParentGuardian);

      if (allRoles?.length) {
        let sanitizedAllRoles = [...allRoles];

        //We need to filter out Den chief if unit is a troop
        if (
          program === programForUnitType.Troop &&
          allRoles.includes('Den Chief')
        ) {
          sanitizedAllRoles = allRoles.filter(role => role !== 'Den Chief');
        }

        //We need to filter out Parent/Guardian when the current selected unit is for Leader purpuses
        if (!isOrgParentGuardian && allRoles.includes(parentGuardian)) {
          sanitizedAllRoles = allRoles.filter(role => role !== parentGuardian);
        }

        const permsByPosition = sanitizedAllRoles.map(
          role => newRolePermissions[role] || {},
        );

        // A user aggregating both a lite and a full-roster position in the same
        // org keeps the full roster; lite must yield to VIEW_ROSTER_PAGE.
        acc[organizationGuid] = enforceRosterAccessExclusivity(
          assign({}, ...permsByPosition),
        );
      }

      return acc;
    },
    {},
  ),
);

/**
 * For checking multi uint perms in router (mostly event form routes)
 */
export const hasNewPermissionInAnyUnitSel = (state, ...permissions) =>
  Object.values(newOrgPermissionsSel(state) ?? {}).some(orgPerms =>
    permissions.some(p => !!orgPerms[p]),
  );

/**
 *  You can use this function to know if the user has 1 or multiple permissions
 * from a unit that is not the currently selected
 * @param {*} state
 * @param {string[]} unitsOrgGuids
 * @param {Array<keyof typeof newPermissions>} permissions
 * @returns {boolean} If it has every permission passed for every unit passed
 */
export const hasNewPermissionInUnitsSel = (
  state,
  unitsOrgGuids = [],
  permissions = [],
) => {
  const userData = userDataSel(state);
  const allRolesByUnit = userData?.organizations?.reduce((acc, org) => {
    if (unitsOrgGuids.includes(org.organizationGuid) && org?.allRoles?.length) {
      return [...acc, org.allRoles];
    }
    return acc;
  }, []);

  if (allRolesByUnit?.length) {
    return allRolesByUnit.every(unitRoles => {
      const permsByPosition = unitRoles.map(
        role => newRolePermissions[role] || {},
      );
      const unitPermissions = permsByPosition.reduce(
        (resultPerms, currentValue) => ({ ...resultPerms, ...currentValue }),
        {},
      );

      const hasPermission = permissions.every(
        permission => !!unitPermissions[permission],
      );
      return hasPermission;
    });
  }
  return false;
};

export const urlUserIdSel = createSelector(
  payloadSel,
  payload => +payload.userId,
);

const canEditAllSubUnitsSel = state =>
  hasNewPermissionSel(state, newPermissions.EDIT_UNIT);

// The sub unit permission used to be only 1
// I am putting both in here to keep the same logic, but change it to use only 1 if needed
const canEditOnlyMySubUnitsSel = state =>
  hasNewPermissionSel(
    state,
    newPermissions.EDIT_SUB_UNIT,
    newPermissions.EDIT_SUB_PROFILE,
  );

export const shouldFilterUsersBySubunitsSel = createSelector(
  canEditAllSubUnitsSel,
  canEditOnlyMySubUnitsSel,
  selectedOrganizationSel,
  (canEditAllSubUnits, canEditMySubUnits, selectedOrganization) => {
    const { unitTypeId } = selectedOrganization;
    const hasSubUnitOnlyPerms = canEditMySubUnits;

    const shouldFilterUsersBySubunits =
      [unitTypeIdForUnitType.Troop, unitTypeIdForUnitType.Pack].includes(
        Number(unitTypeId),
      ) &&
      hasSubUnitOnlyPerms &&
      !canEditAllSubUnits;
    return shouldFilterUsersBySubunits;
  },
);

const canViewMBCSearchPageSel = state =>
  hasNewPermissionSel(state, newPermissions.VIEW_MERIT_BADGE_COUNSELORS_PAGE);

export const shouldShowMBCSearchPageSel = createSelector(
  canViewMBCSearchPageSel,
  organizationPositionSel,
  (canViewMBCSearchPage, organizationPosition) => {
    if (!featureFlags.getFlag('SBL_5804_SHOW_MBC_SEARCH_PAGE')) return false;

    if (
      [Program.EXPLORING, Program.CUB_SCOUT, Program.CLUB].includes(
        organizationPosition.program,
      )
    )
      return false;

    if (canViewMBCSearchPage) return true;

    return false;
  },
);
